import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertOctagon, Mail, ShieldOff, Copy, Send, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * AccessSuspended — the formal legal-notice page shown when a user whose
 * status is ``deactivated`` attempts to reach any platform surface.
 *
 * Design brief: obsidian background, gold monumental type, crimson accent
 * for the revocation stamp, definitive tone.  This is not a soft "verification
 * pending" screen — it's a court-style notice.
 */
export default function AccessSuspended() {
  const { user, logout, api } = useAuth();
  const nav = useNavigate();
  const ref = user?.deactivation_ref || (user?.id ? `VNC/REV/${new Date().getFullYear()}/${String(user.id).slice(-6).toUpperCase()}` : "—");
  const revokedAt = user?.deactivated_at ? new Date(user.deactivated_at) : null;
  const reason = user?.deactivation_reason || "";

  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existing, setExisting] = useState(null);   // any pending request

  useEffect(() => {
    if (!api || !user) return;
    api.get("/reinstatement-requests/mine").then((r) => setExisting(r.data?.request || null)).catch(() => {});
  }, [api, user]);

  const submitRequest = async () => {
    if (!api) return;
    setSubmitting(true);
    try {
      const r = await api.post("/reinstatement-requests", { note });
      setExisting({ id: r.data.id, status: "pending", note, case_ref: r.data.case_ref });
      toast.success("Reinstatement request submitted to the Vinci Trade Desk");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit request");
    } finally { setSubmitting(false); }
  };

  const doLogout = async () => { await logout(); nav("/", { replace: true }); };
  const copyRef = () => { navigator.clipboard.writeText(ref); toast.success("Case reference copied"); };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F5F5F0]" data-testid="access-suspended-page">
      {/* Copper hairline top */}
      <div className="h-[3px] w-full bg-gradient-to-r from-transparent via-[#C0392B] to-transparent opacity-60" />

      <div className="max-w-4xl mx-auto px-6 md:px-10 py-14 md:py-20">
        {/* Case header */}
        <div className="flex flex-wrap items-start justify-between gap-6 mb-10 pb-8 border-b border-white/10">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo-mark.png" alt="Vinci" className="h-7 w-auto" />
              <span className="font-grotesk text-[10px] tracking-[0.32em] uppercase text-[#8A8A82]">Vinci Trade Network</span>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <ShieldOff size={22} className="text-[#C0392B]" />
              <span className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#C0392B]">Formal Notice · Access Revoked</span>
            </div>
          </div>
          <div className="text-right">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#8A8A82]">Case Reference</div>
            <button
              onClick={copyRef}
              data-testid="access-suspended-copy-ref"
              className="mt-1 inline-flex items-center gap-2 font-mono text-lg text-[#D8B23E] hover:opacity-80"
            >{ref} <Copy size={13} className="opacity-60" /></button>
            {revokedAt && (
              <div className="font-body text-xs text-[#8A8A82] mt-1">
                Effective {revokedAt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}
              </div>
            )}
          </div>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="font-serif text-4xl md:text-6xl leading-tight"
        >
          Your access to the Vinci Trade Network<br/>
          <span className="text-[#C0392B]">has been revoked.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.6 }}
          className="font-body text-base md:text-lg text-[#C9C9C2] mt-6 leading-relaxed max-w-3xl"
        >
          This is a formal notice from the Vinci Trade Desk. On the effective date
          above, the executive authority of Vinci Inc. has revoked your operating
          access to the Vinci Trade Network. All future trading, ordering,
          settlement and communication channels have been disabled with
          immediate effect.
        </motion.p>

        {reason && (
          <div className="mt-8 border-l-2 border-[#C0392B] pl-5 py-1 max-w-3xl" data-testid="access-suspended-reason">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#C0392B] mb-2">Reason on record</div>
            <p className="font-body text-[15px] text-[#F5F5F0] leading-relaxed italic">"{reason}"</p>
          </div>
        )}

        {/* Statute strip */}
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { k: "01", t: "Ledger obligations remain", d: "All open orders, invoices, agreements and settlements associated with your Vinci Code remain valid and legally enforceable under the Master Agreement." },
            { k: "02", t: "Data preserved on record", d: "Your KYC, trade history and financial documents are retained by Vinci Inc. for statutory and regulatory audit periods." },
            { k: "03", t: "Reinstatement by written request", d: "Reinstatement is at the sole discretion of the Vinci Trade Desk following a written request to trade@realtymaze.com quoting the case reference above." },
          ].map((c) => (
            <div key={c.k} className="border border-white/10 p-5 rounded-sm bg-black/40">
              <div className="font-mono text-[10px] text-[#D8B23E] tracking-[0.22em]">{c.k}</div>
              <div className="font-serif text-lg text-[#F5F5F0] mt-1">{c.t}</div>
              <p className="font-body text-[13px] text-[#8A8A82] mt-2 leading-relaxed">{c.d}</p>
            </div>
          ))}
        </div>

        {/* Reinstatement request form (in-app) */}
        {user && (
          <div className="mt-12 border border-white/10 rounded-sm p-6 md:p-7 bg-black/40" data-testid="reinstatement-form">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-1.5 h-4 bg-[#D8B23E]" />
              <span className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#D8B23E]">Formal Reinstatement Request</span>
            </div>
            {existing?.status === "pending" ? (
              <div className="flex items-start gap-3 text-[#C9C9C2]" data-testid="reinstatement-existing">
                <CheckCircle2 size={20} className="text-[#2FB999] mt-0.5 shrink-0" />
                <div>
                  <div className="font-body text-base text-[#F5F5F0]">Your request is under review.</div>
                  <div className="font-body text-[13px] text-[#8A8A82] mt-1">
                    The Vinci Trade Desk has received your request under case reference{" "}
                    <span className="font-mono text-[#D8B23E]">{existing.case_ref || ref}</span>. You will receive a formal decision by email.
                  </div>
                </div>
              </div>
            ) : (
              <>
                <p className="font-body text-[13px] text-[#8A8A82] mb-3 leading-relaxed">
                  Provide a brief written statement in support of your request. Your submission is logged
                  against case reference <span className="font-mono text-[#D8B23E]">{ref}</span> and reviewed by the Vinci Trade Desk.
                </p>
                <textarea
                  data-testid="reinstatement-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="e.g. All outstanding dues have been cleared as per settlement dated 12 Feb 2026. Requesting review at your earliest convenience."
                  className="w-full bg-[#050505] border border-white/15 rounded-sm px-4 py-3 font-body text-[14px] text-[#F5F5F0] outline-none focus:border-[#D8B23E] resize-none"
                />
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <span className="font-body text-[10px] text-[#5f5f58]">{note.length}/2000</span>
                  <button
                    data-testid="reinstatement-submit"
                    onClick={submitRequest}
                    disabled={submitting || !note.trim()}
                    className="inline-flex items-center gap-2 rounded-sm bg-gradient-to-r from-[#D8B23E] to-[#B87333] text-black px-6 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.28em] disabled:opacity-40"
                  >
                    {submitting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    Submit Request
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* CTAs */}
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            data-testid="access-suspended-mail-desk"
            href={`mailto:trade@realtymaze.com?subject=Reinstatement%20Request%20-%20${encodeURIComponent(ref)}&body=Dear%20Vinci%20Trade%20Desk%2C%0A%0AI%20wish%20to%20submit%20a%20formal%20request%20for%20reinstatement%20of%20my%20access%20to%20the%20Vinci%20Trade%20Network%20under%20Case%20Reference%20${encodeURIComponent(ref)}.%0A%0ARegards%2C`}
            className="inline-flex items-center gap-2 rounded-sm bg-gradient-to-r from-[#C0392B] to-[#8A2C22] text-white px-6 py-3 font-grotesk text-[11px] uppercase tracking-[0.28em] hover:opacity-95"
          >
            <Mail size={13} /> Request Reinstatement
          </a>
          <button
            data-testid="access-suspended-signout"
            onClick={doLogout}
            className="rounded-sm border border-white/25 text-[#C9C9C2] hover:text-white hover:border-white px-6 py-3 font-grotesk text-[11px] uppercase tracking-[0.28em]"
          >
            Sign Out
          </button>
        </div>

        {/* Footer */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap justify-between gap-6">
          <div className="font-body text-[11px] text-[#5f5f58] leading-relaxed max-w-md">
            Vinci Inc. · 6th Floor, WeWork Lightbridge, Hiranandani Business Park,
            Saki Vihar Road, Chandivali / Powai, Mumbai Suburban, Maharashtra 400072, IN
            <br/>trade@realtymaze.com · 022 4148 5688
          </div>
          <div className="text-right">
            <div className="font-grotesk text-[9px] tracking-[0.32em] text-[#C0392B] uppercase">Notice serial</div>
            <div className="font-mono text-[11px] text-[#8A8A82] mt-1">{ref}</div>
          </div>
        </div>

        <Link to="/" className="mt-6 inline-block font-body text-[11px] text-[#8A8A82] hover:text-[#D8B23E]">
          ← Return to homepage
        </Link>
      </div>

      {/* Watermark seal */}
      <div className="fixed right-4 bottom-4 pointer-events-none opacity-[0.05] rotate-12">
        <AlertOctagon size={240} className="text-[#C0392B]" />
      </div>
    </div>
  );
}
